'use client'

import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { CardStyleLarge } from './CardStyleLarge'
import { CardStyleStandard } from './CardStyleStandard'
import { CardStyleStandardAccordion } from './CardStyleStandardAccordion'
import { CardStyleTopContent } from './CardStyleTopContent'
import { CardStyleReveal } from './CardStyleReveal'
import { ToggleButton } from '@/components/ui/toggle-button'
import { CTAButton } from '@/components/ui/cta-button'
import { mapLegacyAppearance } from '@/fields/link'
import { resolveHref } from '@/utilities/resolveHref'
import type { CardsBlock as CardsBlockProps, Media } from '@/payload-types'
import Image from 'next/image'
import { cn } from '@/utilities/ui'
import { getBlockPadding } from '@/utilities/getBlockPadding'

// Type guard to safely check if an object is a Media type
function isMediaObject(media: unknown): media is Media {
  return (
    typeof media === 'object' &&
    media !== null &&
    ('url' in media || 'filename' in media)
  )
}

// Type-safe utility to get URL from Media object or string
function getMediaUrl(media: Media | string | null | undefined): string | null {
  if (!media) return null
  if (typeof media === 'string') return media
  if (isMediaObject(media)) {
    return media.url || (media.filename ? `/media/${media.filename}` : null)
  }
  return null
}

// Type-safe utility to handle icon URL resolution
function getIconUrl(icon: NonNullable<CardsBlockProps['cards']>[0]['icon']): string | null {
  if (!icon) return null
  if (typeof icon === 'string') return null // This is an ID reference, not useful for URL
  return getMediaUrl(icon)
}

// Type-safe utility to create icon props for card components
function getIconProps(icon: NonNullable<CardsBlockProps['cards']>[0]['icon']) {
  if (!icon || typeof icon === 'string') return undefined
  if (isMediaObject(icon)) {
    return {
      url: icon.url || undefined,
      filename: icon.filename || undefined,
      updatedAt: icon.updatedAt || undefined
    }
  }
  return undefined
}

type Props = CardsBlockProps & {
  disableInnerContainer?: boolean
}

export const CardsBlock: React.FC<Props> = ({
  style = 'large',
  mobileView = 'stacked',
  cards = [],
  disableInnerContainer: _disableInnerContainer,
  paddingTop,
  paddingBottom,
}) => {
  // Get padding classes based on layout options
  const paddingClasses = getBlockPadding({
    paddingTop: paddingTop ?? true,
    paddingBottom: paddingBottom ?? true,
  })
  // State for mobile accordion - responsive initial state
  const [expandedCardIndex, setExpandedCardIndex] = useState<number | null>(null)
  // State for hover effects in Large Cards
  const [hoveredCard, setHoveredCard] = useState<number | null>(null)

  // Height synchronization for large cards on desktop
  const [titleHeight, setTitleHeight] = useState<number>(0)
  const [descriptionHeight, setDescriptionHeight] = useState<number>(0)
  const titleRefs = useRef<(HTMLDivElement | null)[]>([])
  const descriptionRefs = useRef<(HTMLDivElement | null)[]>([])

  // Measure and sync heights for large cards on desktop
  useLayoutEffect(() => {
    if (style !== 'large') return
    if (typeof window === 'undefined') return

    const measureHeights = () => {
      // Only sync on desktop (lg breakpoint = 1024px)
      if (window.innerWidth < 1024) {
        // Reset heights on mobile so they don't affect layout
        setTitleHeight(0)
        setDescriptionHeight(0)
        return
      }

      // Temporarily reset heights to allow natural measurement
      setTitleHeight(0)
      setDescriptionHeight(0)

      // Use requestAnimationFrame to measure after reset is applied
      requestAnimationFrame(() => {
        const maxTitleHeight = Math.max(
          ...titleRefs.current.map((el) => el?.offsetHeight || 0)
        )
        const maxDescHeight = Math.max(
          ...descriptionRefs.current.map((el) => el?.offsetHeight || 0)
        )

        if (maxTitleHeight > 0) setTitleHeight(maxTitleHeight)
        if (maxDescHeight > 0) setDescriptionHeight(maxDescHeight)
      })
    }

    // Measure on mount/update
    measureHeights()

    // Re-measure on any window resize (debounced)
    let resizeTimeout: ReturnType<typeof setTimeout>
    const handleResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(measureHeights, 100)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(resizeTimeout)
    }
  }, [cards, style])

  // Set initial expanded state based on screen size
  useEffect(() => {
    const checkScreenSize = () => {
      // Desktop breakpoint is 'md' (768px) to match the component's responsive breakpoint
      const isDesktop = window.matchMedia('(min-width: 768px)').matches
      // Start with the last card expanded on mobile
      setExpandedCardIndex(isDesktop ? null : (cards?.length || 1) - 1)
    }

    // Check on mount
    checkScreenSize()

    // Optional: Listen for screen size changes (if user resizes window)
    const mediaQuery = window.matchMedia('(min-width: 768px)')
    const handleChange = () => checkScreenSize()
    mediaQuery.addEventListener('change', handleChange)

    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [cards?.length])

  const handleCardToggle = (index: number) => {
    setExpandedCardIndex(expandedCardIndex === index ? null : index)
  }

  // Large Cards: CloudFeaturesBlock-inspired layout with hover effects
  if (style === 'large') {

    return (
      <div className={cn('box-border content-stretch flex flex-col gap-[42px] items-start justify-start relative size-full', paddingClasses)}>
        {/* Desktop Layout - Horizontal 3-column */}
        <div className="hidden lg:flex gap-[22px] items-end justify-start w-full">
          {cards?.map((card, index) => (
            <CardStyleLarge
              key={index}
              title={card.title}
              description={card.description || undefined}
              icon={getIconProps(card.icon)}
              cta={card.cta || undefined}
              cardIndex={index}
              isHovered={hoveredCard === index}
              onHover={(isHovered) => setHoveredCard(isHovered ? index : null)}
              titleRef={(el) => {
                titleRefs.current[index] = el
              }}
              descriptionRef={(el) => {
                descriptionRefs.current[index] = el
              }}
              titleHeight={titleHeight}
              descriptionHeight={descriptionHeight}
            />
          ))}
        </div>

        {/* Mobile Layout - Accordion behavior */}
        <div className="flex 2 flex-col gap-2 w-full lg:hidden">
          {cards?.map((card, index) => {
            const isExpanded = expandedCardIndex === index
            const iconUrl = getIconUrl(card.icon)

            return (
              <div
                key={index}
                className={`box-border content-stretch flex flex-col rounded-[16px] w-full transition-colors ${
                  isExpanded ? 'bg-civo-cloud p-7' : 'border border-civo-cloud p-6'
                }`}
              >
                {!isExpanded ? (
                  // Collapsed state
                  <div className="flex items-center justify-between w-full gap-4">
                    <span className="display-x-small text-black truncate">{card.title}</span>
                    <ToggleButton
                      isExpanded={false}
                      onClick={() => handleCardToggle(index)}
                      selectedColor="black"
                      unselectedColor="lime"
                    />
                  </div>
                ) : (
                  // Expanded state
                  <div className="flex flex-col gap-10 w-full">
                    <div className="flex items-start justify-between w-full">
                      <div className="flex items-start">
                        {iconUrl && (
                          <Image
                            src={iconUrl}
                            alt={card.title}
                            width={88}
                            height={88}
                            className="h-[88px] w-auto object-contain"
                          />
                        )}
                      </div>
                      <ToggleButton
                        isExpanded={true}
                        onClick={() => handleCardToggle(index)}
                        selectedColor="black"
                        unselectedColor="lime"
                      />
                    </div>
                    <div className="flex flex-col gap-3">
                      <div className="display-x-small text-black">
                        <p className="leading-8 mb-0">{card.title}</p>
                      </div>
                      {card.description && (
                        <div className="body-regular text-black/70">
                          <p className="leading-[22px] mb-0">{card.description}</p>
                        </div>
                      )}
                      {card.cta?.label && (card.cta?.url || card.cta?.reference) && (
                        <div className="mt-2">
                          <CTAButton
                            variant={mapLegacyAppearance(card.cta.appearance)}
                            href={resolveHref(card.cta)}
                            newTab={card.cta.newTab || false}
                          >
                            {card.cta.label}
                          </CTAButton>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // Standard Cards: Simple cards layout with mobile view options
  if (style === 'standard') {
    // Accordion mobile view for Standard Cards
    if (mobileView === 'accordion') {
      return (
        <CardStyleStandardAccordion
          cards={cards || []}
          paddingTop={paddingTop ?? undefined}
          paddingBottom={paddingBottom ?? undefined}
        />
      )
    }

    // Stacked mobile view for Standard Cards (default)
    return (
      <div className={cn('box-border content-stretch flex flex-col gap-7 items-start justify-start relative w-full lg:gap-[62px]', paddingClasses)}>
        <div className="content-stretch flex flex-col gap-2 items-start justify-start relative w-full md:grid md:grid-cols-2 md:gap-6 lg:flex lg:flex-row lg:gap-[22px] lg:items-stretch lg:justify-start">
          {cards?.map((card, index) => (
            <CardStyleStandard
              key={index}
              title={card.title}
              backgroundImage={card.backgroundImage}
              description={card.description || undefined}
              cta={card.cta || undefined}
              isExpanded={expandedCardIndex === index}
              onToggle={() => handleCardToggle(index)}
              cardIndex={index}
            />
          ))}
        </div>
      </div>
    )
  }

  // Top Content: Hero-style cards with background images (always expanded)
  if (style === 'topContent') {
    return (
      <div className={cn('box-border content-stretch flex flex-col gap-7 items-start justify-start relative w-full lg:gap-[62px]', paddingClasses)}>
        {/* Cards Section */}
        <div className="content-stretch flex flex-col gap-2 items-start justify-start relative w-full lg:flex-row lg:gap-[22px] lg:items-end lg:justify-between">
          {cards?.map((card, index) => (
            <CardStyleTopContent
              key={index}
              title={card.title}
              backgroundImage={card.backgroundImage || undefined}
              description={card.description || undefined}
              cta={card.cta || undefined}
              isExpanded={true}
              onToggle={() => {}}
              cardIndex={index}
            />
          ))}
        </div>
      </div>
    )
  }

  // Reveal Cards: Animation cards with title at bottom, description reveals on hover
  if (style === 'reveal') {
    return (
      <div className={cn('box-border content-stretch flex flex-col gap-7 items-start justify-start relative w-full lg:gap-[62px]', paddingClasses)}>
        <div className="content-stretch flex flex-col gap-2 items-start justify-start relative w-full md:grid md:grid-cols-2 md:gap-6 lg:flex lg:flex-row lg:gap-[22px] lg:items-stretch lg:justify-start">
          {cards?.map((card, index) => (
            <CardStyleReveal
              key={index}
              title={card.title}
              backgroundImage={card.backgroundImage}
              description={card.description || undefined}
              cta={card.cta || undefined}
              isExpanded={expandedCardIndex === index}
              onToggle={() => handleCardToggle(index)}
            />
          ))}
        </div>
      </div>
    )
  }

  // Fallback to Large Cards as default
  return (
    <div className={cn('box-border content-stretch flex flex-col gap-[42px] items-start justify-start relative size-full', paddingClasses)}>
      {/* Desktop Layout - Horizontal 3-column */}
      <div className="hidden lg:flex gap-[22px] items-end justify-start">
        {cards?.map((card, index) => (
          <CardStyleLarge
            key={index}
            title={card.title}
            description={card.description || undefined}
            icon={getIconProps(card.icon)}
            cta={card.cta || undefined}
            cardIndex={index}
            isHovered={hoveredCard === index}
            onHover={(isHovered) => setHoveredCard(isHovered ? index : null)}
          />
        ))}
      </div>

      {/* Mobile Layout - Accordion behavior */}
      <div className="flex flex-col gap-2 w-full lg:hidden">
        {cards?.map((card, index) => {
          const isExpanded = expandedCardIndex === index
          const iconUrl = getIconUrl(card.icon)

          return (
            <div
              key={index}
              className={`box-border content-stretch flex flex-col rounded-[16px] w-full transition-colors ${
                isExpanded ? 'bg-civo-cloud p-6' : 'border border-civo-cloud p-6'
              }`}
            >
              {!isExpanded ? (
                // Collapsed state
                <div className="flex items-center justify-between w-full gap-4">
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    {iconUrl && (
                      <div className="relative h-10 w-10 shrink-0 rounded-lg">
                        <Image
                          src={iconUrl}
                          alt={card.title}
                          fill
                          className="object-contain"
                          sizes="40px"
                        />
                      </div>
                    )}
                    <div className="display-xsmall text-black min-w-0">
                      <p className="leading-8 mb-0 truncate">{card.title}</p>
                    </div>
                  </div>
                  <ToggleButton
                    isExpanded={false}
                    onClick={() => handleCardToggle(index)}
                    selectedColor="black"
                    unselectedColor="lime"
                  />
                </div>
              ) : (
                // Expanded state
                <div className="flex flex-col gap-10 w-full">
                  <div className="flex items-start justify-between w-full">
                    <div className="flex items-start">
                      {iconUrl && (
                        <div className="relative h-[88px] min-w-[88px]">
                          <Image
                            src={iconUrl}
                            alt={card.title}
                            fill
                            className="object-contain"
                            sizes="88px"
                          />
                        </div>
                      )}
                    </div>
                    <ToggleButton
                      isExpanded={true}
                      onClick={() => handleCardToggle(index)}
                      selectedColor="black"
                      unselectedColor="lime"
                    />
                  </div>
                  <div className="flex flex-col gap-3">
                    <div className="display-xsmall text-black">
                      <p className="leading-8 mb-0">{card.title}</p>
                    </div>
                    {card.description && (
                      <div className="body-medium text-black/70">
                        <p className="leading-[22px] mb-0">{card.description}</p>
                      </div>
                    )}
                    {card.cta?.label && (card.cta?.url || card.cta?.reference) && (
                      <div className="mt-2">
                        <CTAButton
                          variant={mapLegacyAppearance(card.cta.appearance)}
                          href={resolveHref(card.cta)}
                          newTab={card.cta.newTab || false}
                        >
                          {card.cta.label}
                        </CTAButton>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}